import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';

const CoverUploader = ({
  pathId,
  onUploaded,
}: {
  pathId: string;
  onUploaded: (url: string | null) => void;
}) => {
  const { toast } = useToast();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const filePath = `path-covers/${pathId}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('covers')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      toast({ title: 'Upload failed', description: uploadError.message, variant: 'destructive' });
      return;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from('covers').getPublicUrl(filePath);

    const { error: updateError } = await supabase
      .from('paths')
      .update({ cover_image_url: publicUrl })
      .eq('id', pathId);

    if (updateError) {
      toast({ title: 'Update failed', description: updateError.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Cover updated' });
    onUploaded(publicUrl);
  };

  const handleDelete = async () => {
    const filePath = `path-covers/${pathId}.jpg`;

    const { error: removeError } = await supabase.storage
      .from('covers')
      .remove([filePath]);

    if (removeError) {
      toast({ title: 'Delete failed', description: removeError.message, variant: 'destructive' });
      return;
    }

    const { error: updateError } = await supabase
      .from('paths')
      .update({ cover_image_url: null })
      .eq('id', pathId);

    if (updateError) {
      toast({ title: 'Update failed', description: updateError.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Cover removed' });
    onUploaded(null);
  };

  return (
    <div className="space-y-2">
      <Input type="file" accept="image/*" onChange={handleUpload} />
      <Button variant="destructive" onClick={handleDelete} size="sm">
        Remove Cover
      </Button>
    </div>
  );
};

export default CoverUploader;
